/// Utilities for getting the organization of documentation pages. Should be
/// used to build the navigation for documentation.

import { slugForString } from 'utils/strings';

import docs from '../navigation/docs/docs.json';

type Page = {
  name: string;
  slug?: string;
  version?: number;
  pages?: Page[];
  urlPath?: string;
  path?: string;
  pdfFreeze?: boolean;
};

// Get the slug for a page, or create one from the name.
function slugForPage(page: Page): string {
  return page.slug || slugForString(page.name);
}

type BlockFn = (page: Page, ancestors: Page[]) => Page;

/// A nested map through the page objects. Use to add slugs and paths to pages
/// without mutating hte source.
function mapPages(pages: Page[], block: BlockFn, ancestors: Page[] = []) {
  return pages.map((page) => {
    const mappedPage = block(page, ancestors);
    if (page.pages != null) {
      mappedPage.pages = mapPages(page.pages, block, [...ancestors, page]);
    }
    return mappedPage;
  });
}

/// Returns a cloned set of pages with names, slugs, and paths generated based
/// on the slugs of parent pages.
function pagesAddingPaths(pages: Page[], prefix: string) {
  return mapPages(pages, (page, ancestors) => {
    const path = `${prefix}${[...ancestors, page].map(slugForPage).join('/')}/`;
    return {
      name: page.name,
      slug: slugForPage(page),
      version: page.version,
      path,
    };
  });
}

const products: Page[] = docs.pages.map((product: Page) => {
  product.pages = pagesAddingPaths(product.pages || [], '/docs/');
  return product;
});

/// Return the product, version, and nested documentation pages for the given
/// product and path, e.g. "/docs/v3/control/midi-file-cues". `pages` will
/// include all pages for the version.
///
/// This method is populated by the `src/data/docs/docs.json` file, but pages
/// are normalized to all include slugs and paths.
export function productForPath(
  productSlug: string,
  path: string,
): {
  product?: Page;
  version?: Page;
  productVersions?: Page[];
  pages?: Page[];
} {
  const [, , versionSlug] = path.split('/');

  const product = products.find((page) => page.slug === productSlug);

  const version = product?.pages?.find((page) => page.slug === versionSlug);

  const productVersions = product && product.pages;

  const pages = version?.pages;

  return {
    product,
    version,
    productVersions,
    pages,
  };
}
